<template>
  <div class="record-details" v-if="record">
    <div class="header">
      <h2>{{ isEditMode ? "Edit Record" : record.name }}</h2>

      <div class="top-buttons">
        <!-- Graph toggle -->
        <button
          @click="toggleGraph"
          class="icon-btn"
          :title="showGraph ? 'Hide connections' : 'Show connections'"
        >
          <i class="bi bi-link-45deg"></i>
        </button>
        <button @click="toggleEditMode" class="icon-btn">
          <i class="bi bi-pencil"></i>
        </button>
        <button class="remove-btn" @click="deleteRecord">
          <i class="bi bi-trash"></i>
        </button>
      </div>
    </div>

    <!-- Graph connections zone -->
    <div v-if="showGraph">
      <!-- When adding connections -->
      <AddGraphConnection
        v-if="showAddConnections"
        :sourceNodeId="record.node_id"
        @cancel="showAddConnections = false"
        @connected="handleConnectionCreated"
      />
      <!-- When just listing connections -->
      <GraphPanel
        v-else
        :key="graphPanelKey"
        :nodeId="record.node_id"
        @addRequested="showAddConnections = true"
        @closeRequested="showGraph = false"
      />
    </div>

    <!-- Display mode -->
    <div v-else-if="!isEditMode">
      <div class="detail"><strong>ID:</strong> {{ record.node_id }}</div>
      <div class="detail"><strong>Name:</strong> {{ record.name }}</div>
      <div class="detail"><strong>Type:</strong> {{ getRecordTypeName(record.record_type_id) }}</div>
      <div class="detail"><strong>Owner:</strong> {{ username || "N/A" }}</div>

      <section v-if="showMetadata" style="margin-top:16px">
        <NodeMdList
          :key="record.node_id"
          assType="record"
          :assId="record.node_id"
        />
      </section>
    </div>

    <!-- Edit mode -->
    <div v-else>
      <div class="form-group">
        <label for="name">Name</label>
        <input id="name" type="text" v-model="editableRecord.name" class="form-control" />
      </div>

      <div class="form-group">
        <label for="record_type_id">Type</label>
        <select id="record_type_id" v-model.number="editableRecord.record_type_id" class="form-control">
          <option v-for="type in recordTypes" :key="type.id" :value="type.id">
            {{ type.name }}
          </option>
        </select>
      </div>

      <div class="buttons-container">
        <button @click="saveChanges">Save</button>
        <button @click="cancelEditMode">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import NodeMdList from "@/components/NodeMdList.vue";
import GraphPanel from "@/components/GraphPanel.vue";
import AddGraphConnection from "@/components/AddGraphConnection.vue";
import apiService from "@/services/apiService";
import { API_ENDPOINTS } from "@/services/config";
import { confirmDelete } from "@/utils/confirmDelete";
import { mapState } from "vuex";

export default {
  components: {
    NodeMdList,
    GraphPanel,
    AddGraphConnection,
  },
  props: {
    record: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    showMetadata: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isEditMode: false,
      editableRecord: {},
      username: null,
      recordTypes: [],
      recordTypeMap: {},
      showGraph: false,
      showAddConnections: false,
      graphPanelKey: 0,
    };
  },
  computed: {
    ...mapState(["loggedInUser"]),
  },
  created() {
    this.fetchRecordTypes();
    this.fetchUserName(this.record.user_id);
  },
  watch: {
    record: {
      handler() {
        this.fetchUserName(this.record.user_id);
      },
      deep: true,
    },
  },
  methods: {
    async fetchRecordTypes() {
      try {
        const response = await apiService.fetchResources(
          API_ENDPOINTS.recordTypes,
          this.loggedInUser?.entra_id
        );
        this.recordTypes = Array.isArray(response) ? response : [];
        
        // Build quick lookup map
        this.recordTypeMap = {};
        this.recordTypes.forEach(type => {
          this.recordTypeMap[type.id] = type.name;
        });
      } catch (error) {
        console.error("Error fetching record types:", error);
      }
    },

    getRecordTypeName(typeId) {
      return this.recordTypeMap[typeId] || `Type ${typeId}`;
    },

    async fetchUserName(userId) {
      if (!userId) {
        this.username = null;
        return;
      }
      try {
        const user = await apiService.fetchResources(API_ENDPOINTS.user(userId));
        this.username = user.username;
      } catch (error) {
        console.error("Error fetching user:", error);
        this.username = "Unknown user";
      }
    },

    toggleEditMode() {
      this.isEditMode = true;
      this.editableRecord = JSON.parse(JSON.stringify(this.record));
    },

    toggleGraph() {
      // toggling off should also exit add mode
      if (this.showGraph) this.showAddConnections = false;
      this.showGraph = !this.showGraph;
    },

    handleConnectionCreated() {
      // come back to the list view and refresh it
      this.showAddConnections = false;
      // bump key to remount GraphPanel so it refetches
      this.graphPanelKey += 1;
    },

    async saveChanges() {
      try {
        const payload = {
          name: this.editableRecord.name,
          record_type_id: this.editableRecord.record_type_id,
        };

        await axios.put(
          API_ENDPOINTS.record(this.record.node_id),
          { ...payload, entra_id: this.loggedInUser.entra_id },
          { headers: { "Content-Type": "application/json" } }
        );

        this.$emit("updateRecord", this.editableRecord);
        this.isEditMode = false;
      } catch (error) {
        console.error("Error saving record:", error);
      }
    },

    cancelEditMode() {
      this.isEditMode = false;
    },

    async deleteRecord() {
      try {
        const isConfirmed = await confirmDelete(
          "Are you sure you want to delete this Record?"
        );
        if (!isConfirmed) return;

        await axios.delete(
          API_ENDPOINTS.record(this.record.node_id),
          {
            data: { entra_id: this.loggedInUser.entra_id },
            headers: { "Content-Type": "application/json" },
          }
        );

        this.$emit("deleted");
      } catch (error) {
        console.error("Error deleting record:", error);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.record-details {
  max-width: 800px;
  margin: 0 auto;
  padding: 15px 20px;
  border-radius: 8px;
  background-color: $secondary-color;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .buttons-container {
    display: flex;
    justify-content: space-between;
    width: 100%;
    align-items: flex-end;
  }

  .top-buttons {
    display: flex;
    gap: 10px;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    h2 {
      color: $primary-color;
      margin-bottom: 20px;
    }
  }

  .detail {
    margin-bottom: 3px;
    font-size: 16px;

    strong {
      color: black;
    }
  }
}
</style>